import { useEffect, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';

/* The real `claude setup-token` session, streamed from the install's own
 * server over a websocket. Nothing is parsed or rebuilt in the UI: the user
 * sees exactly what the CLI shows, clicks the sign-in link it prints, and
 * pastes the code back into the terminal itself. When the server spots the
 * minted token in the stream it stores it encrypted and reports connected. */
export function ClaudeSetupTerminal({
  onConnected,
  onExit,
}: {
  onConnected: () => void;
  onExit: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const cbRef = useRef({ onConnected, onExit });
  cbRef.current = { onConnected, onExit };

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const term = new Terminal({
      cols: 100,
      rows: 30,
      fontSize: 12,
      fontFamily: 'ui-monospace, Menlo, monospace',
      cursorBlink: true,
      convertEol: false,
      theme: { background: '#111014', foreground: '#e8e6e1', cursor: '#F5E500' },
    });
    term.loadAddon(new WebLinksAddon());
    term.open(el);
    term.focus();

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/api/platform/claude-credential/setup/terminal`);
    let done = false;

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string);
        if (msg.t === 'o') term.write(msg.d);
        else if (msg.t === 'connected') {
          done = true;
          term.write('\r\n\x1b[32mSubscription connected. You can close this window.\x1b[0m\r\n');
          cbRef.current.onConnected();
        } else if (msg.t === 'error') {
          term.write(`\r\n\x1b[31m${msg.d}\x1b[0m\r\n`);
        } else if (msg.t === 'exit' && !done) {
          term.write('\r\n\x1b[33mThe session ended.\x1b[0m\r\n');
          cbRef.current.onExit();
        }
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = () => {
      if (!done) cbRef.current.onExit();
    };
    const sub = term.onData((d) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'i', d }));
    });

    return () => {
      sub.dispose();
      ws.close();
      term.dispose();
    };
    // The socket and terminal live for the lifetime of the mounted component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={hostRef}
      style={{
        background: '#111014',
        borderRadius: 6,
        padding: 8,
        overflow: 'auto',
        border: '1px solid rgba(128,128,128,0.25)',
      }}
    />
  );
}
